import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { AdminPortalProvider, Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, Spinner } from '@/components/admin/ui';
import {
  IconBolt,
  IconBookOpen,
  IconChartBarSquare,
  IconChevronDown,
  IconDeviceMobile,
  IconGift,
  IconGlobe,
  IconUsers,
  IconUserPlus,
  IconHome,
  IconLink,
  IconMenu,
  IconPaperAirplane,
  IconPuzzle,
  IconSticker,
  IconX,
} from '@/components';

type AdminTab = {
  to: string;
  label: string;
  match: (p: string) => boolean;
  icon: ReactNode;
};

type AdminNavGroup = {
  id: string;
  label: string;
  icon: ReactNode;
  items: AdminTab[];
};

const overviewTab: AdminTab = {
  to: '/admin',
  label: 'Overview',
  match: (p) => p === '/admin' || p === '/admin/',
  icon: <IconHome className="text-current" />,
};

const toolsTab: AdminTab = {
  to: '/admin/engagement',
  label: 'Tools',
  match: (p) => p.startsWith('/admin/engagement'),
  icon: <IconPaperAirplane className="text-current" />,
};

const navGroups: AdminNavGroup[] = [
  {
    id: 'ops',
    label: 'Ops',
    icon: <IconBolt className="text-current" />,
    items: [
      {
        to: '/admin/support',
        label: 'Support',
        match: (p) => p.startsWith('/admin/support'),
        icon: <IconPaperAirplane className="text-current" />,
      },
      {
        to: '/admin/feedback',
        label: 'Feedback',
        match: (p) => p.startsWith('/admin/feedback'),
        icon: <IconPaperAirplane className="text-current" />,
      },
      {
        to: '/admin/devices',
        label: 'Devices',
        match: (p) => p.startsWith('/admin/devices'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/users',
        label: 'Users',
        match: (p) => p.startsWith('/admin/users'),
        icon: <IconUsers className="text-current" />,
      },
      {
        to: '/admin/donations',
        label: 'Donations',
        match: (p) => p.startsWith('/admin/donations'),
        icon: <IconGift className="text-current" />,
      },
      {
        to: '/admin/activity',
        label: 'Activity',
        match: (p) => p.startsWith('/admin/activity'),
        icon: <IconBolt className="text-current" />,
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: <IconBookOpen className="text-current" />,
    items: [
      {
        to: '/admin/games',
        label: 'Games',
        match: (p) => p.startsWith('/admin/games'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/crosswords',
        label: 'Crosswords',
        match: (p) => p.startsWith('/admin/crosswords'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/village-maps',
        label: 'Village Maps',
        match: (p) => p.startsWith('/admin/village-maps'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/sentence-puzzles',
        label: 'Sentences',
        match: (p) => p.startsWith('/admin/sentence-puzzles'),
        icon: <IconPuzzle className="text-current" />,
      },
      {
        to: '/admin/dictionary',
        label: 'Dictionary',
        match: (p) => p.startsWith('/admin/dictionary'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/sticker-packs',
        label: 'Stickers',
        match: (p) => p.startsWith('/admin/sticker-packs'),
        icon: <IconSticker className="text-current" />,
      },
      {
        to: '/admin/flashcard-sets',
        label: 'Flashcards',
        match: (p) => p.startsWith('/admin/flashcard-sets'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/term-sheets',
        label: 'Term Sheets',
        match: (p) => p.startsWith('/admin/term-sheets'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/storybooks',
        label: 'Storybooks',
        match: (p) => p.startsWith('/admin/storybooks'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/featured-resources',
        label: 'Hero',
        match: (p) => p.startsWith('/admin/featured-resources'),
        icon: <IconLink className="text-current" />,
      },
    ],
  },
  {
    id: 'config',
    label: 'Config',
    icon: <IconUserPlus className="text-current" />,
    items: [
      {
        to: '/admin/feature-flags',
        label: 'Flags',
        match: (p) => p.startsWith('/admin/feature-flags'),
        icon: <IconBolt className="text-current" />,
      },
      {
        to: '/admin/app-version',
        label: 'App Version',
        // Exact path only — do not match Analytics → App Versions (`/admin/app-versions`).
        match: (p) => p === '/admin/app-version' || p.startsWith('/admin/app-version/'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/admin-users',
        label: 'Admins',
        match: (p) => p.startsWith('/admin/admin-users'),
        icon: <IconUserPlus className="text-current" />,
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <IconChartBarSquare className="text-current" />,
    items: [
      {
        to: '/admin/cohorts',
        label: 'Cohorts',
        match: (p) => p.startsWith('/admin/cohorts'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/device-info',
        label: 'Device info',
        match: (p) => p.startsWith('/admin/device-info'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/acquisition',
        label: 'Acquisition',
        match: (p) => p.startsWith('/admin/acquisition'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/metrics',
        label: 'Metrics',
        match: (p) => p.startsWith('/admin/metrics'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/web',
        label: 'Web',
        match: (p) => p.startsWith('/admin/web'),
        icon: <IconGlobe className="text-current" />,
      },
      {
        to: '/admin/app-versions',
        label: 'App Versions',
        match: (p) => p.startsWith('/admin/app-versions'),
        icon: <IconDeviceMobile className="text-current" />,
      },
      {
        to: '/admin/learning',
        label: 'Learning',
        match: (p) => p.startsWith('/admin/learning'),
        icon: <IconBookOpen className="text-current" />,
      },
      {
        to: '/admin/hero-cards',
        label: 'Hero Cards',
        match: (p) => p.startsWith('/admin/hero-cards'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/streaks',
        label: 'Streaks',
        match: (p) => p.startsWith('/admin/streaks'),
        icon: <IconBolt className="text-current" />,
      },
      {
        to: '/admin/village',
        label: 'Village',
        match: (p) => p === '/admin/village' || p.startsWith('/admin/village/'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/analytics/sessions',
        label: 'Sessions',
        match: (p) => p.startsWith('/admin/analytics/sessions'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/analytics/timeline',
        label: 'Timeline',
        match: (p) => p.startsWith('/admin/analytics/timeline'),
        icon: <IconChartBarSquare className="text-current" />,
      },
      {
        to: '/admin/analytics/pulse',
        label: 'Pulse',
        match: (p) => p.startsWith('/admin/analytics/pulse'),
        icon: <IconBolt className="text-current" />,
      },
    ],
  },
];

const allTabs: AdminTab[] = [overviewTab, ...navGroups.flatMap((g) => g.items), toolsTab];

function groupIdForPath(pathname: string) {
  return navGroups.find((group) => group.items.some((tab) => tab.match(pathname)))?.id ?? null;
}

const itemClass = (active: boolean, collapsed: boolean) =>
  clsx(
    'flex h-8 items-center gap-2 rounded-lg text-sm font-medium transition-colors',
    collapsed ? 'w-8 justify-center' : 'px-2',
    active
      ? 'bg-nova-accent text-nova-accent-foreground'
      : 'text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-accent-foreground'
  );

function NavLink({
  tab,
  pathname,
  collapsed,
  onNavigate,
}: {
  tab: AdminTab;
  pathname: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const active = tab.match(pathname);
  return (
    <Link
      to={tab.to}
      title={tab.label}
      aria-current={active ? 'page' : undefined}
      className={itemClass(active, collapsed)}
      onClick={onNavigate}
    >
      <span className="grid h-4 w-4 shrink-0 place-items-center">{tab.icon}</span>
      <span className={clsx('truncate', collapsed && 'sr-only')}>{tab.label}</span>
    </Link>
  );
}

function NavGroup({
  group,
  pathname,
  collapsed,
  open,
  onToggle,
  onNavigate,
}: {
  group: AdminNavGroup;
  pathname: string;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  const groupActive = group.items.some((tab) => tab.match(pathname));

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          title={group.label}
          aria-label={group.label}
          className={clsx(
            'flex h-8 w-8 items-center justify-center rounded-lg text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-accent-foreground',
            groupActive && 'bg-nova-accent text-nova-accent-foreground'
          )}
        >
          <span className="grid h-4 w-4 place-items-center">{group.icon}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[12rem]">
          <div className="px-2 py-1 text-xs font-medium text-nova-muted-foreground">{group.label}</div>
          {group.items.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              role="menuitem"
              className={clsx(
                'flex h-8 items-center gap-2 rounded-md px-2 text-sm',
                tab.match(pathname)
                  ? 'bg-nova-accent text-nova-accent-foreground'
                  : 'text-nova-foreground hover:bg-nova-accent'
              )}
              onClick={onNavigate}
            >
              <span className="grid h-4 w-4 place-items-center">{tab.icon}</span>
              {tab.label}
            </Link>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="flex h-8 w-full items-center justify-between rounded-lg px-2 text-xs font-medium text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-foreground"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{group.label}</span>
        <IconChevronDown size={14} className={clsx('opacity-70 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="mt-0.5 flex flex-col gap-0.5 pl-2">
          {group.items.map((tab) => (
            <NavLink key={tab.to} tab={tab} pathname={pathname} collapsed={false} onNavigate={onNavigate} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SidebarNav({
  pathname,
  collapsed,
  openGroups,
  onToggleGroup,
  onNavigate,
  onSignOut,
  signingOut,
}: {
  pathname: string;
  collapsed: boolean;
  openGroups: Record<string, boolean>;
  onToggleGroup: (id: string) => void;
  onNavigate?: () => void;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  const adminIconSrc = `${import.meta.env.BASE_URL}app-icon.png`;

  return (
    <div className={clsx('flex h-full flex-col bg-nova-sidebar', collapsed ? 'w-14 items-center px-2' : 'w-60 px-2')}>
      <Link
        to={overviewTab.to}
        title="Hmonglus Admin"
        className={clsx(
          'mt-2 flex h-10 items-center gap-2 rounded-lg',
          collapsed ? 'w-8 justify-center' : 'px-1.5'
        )}
        onClick={onNavigate}
      >
        <img
          src={adminIconSrc}
          alt=""
          width={28}
          height={28}
          decoding="async"
          className="h-7 w-7 shrink-0 rounded-lg object-cover"
        />
        <span className={clsx('truncate text-sm font-semibold tracking-tight', collapsed && 'sr-only')}>
          Hmonglus Admin
        </span>
      </Link>

      <nav aria-label="Admin" className="mt-2 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pb-2">
        <NavLink tab={overviewTab} pathname={pathname} collapsed={collapsed} onNavigate={onNavigate} />
        {navGroups.map((group) => (
          <NavGroup
            key={group.id}
            group={group}
            pathname={pathname}
            collapsed={collapsed}
            open={Boolean(openGroups[group.id])}
            onToggle={() => onToggleGroup(group.id)}
            onNavigate={onNavigate}
          />
        ))}
        <NavLink tab={toolsTab} pathname={pathname} collapsed={collapsed} onNavigate={onNavigate} />
      </nav>

      <div className="border-t border-nova-border py-2">
        <button
          type="button"
          title="Sign out"
          className={clsx(
            'flex h-8 items-center gap-2 rounded-lg text-sm text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-foreground',
            collapsed ? 'w-8 justify-center' : 'w-full px-2'
          )}
          disabled={signingOut}
          onClick={onSignOut}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
          </svg>
          <span className={clsx(collapsed && 'sr-only')}>{signingOut ? 'Signing out…' : 'Sign out'}</span>
        </button>
      </div>
    </div>
  );
}

function AdminFrame({ children }: { children: ReactNode }) {
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  return (
    <div ref={setPortalEl} className="admin-portal relative flex min-h-screen w-full bg-nova-background text-nova-foreground">
      <AdminPortalProvider container={portalEl}>{children}</AdminPortalProvider>
    </div>
  );
}

function AdminLayout() {
  const { isReady, isAuthed, isAnon, meError, refetchSession, logout } = useAdminAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const id = groupIdForPath(pathname);
    return id ? { [id]: true } : {};
  });

  useEffect(() => {
    void import('@/styles/admin-portal.css');
  }, []);

  useEffect(() => {
    if (isReady && isAnon) {
      void navigate({ to: '/login' });
    }
  }, [isReady, isAnon, navigate]);

  useEffect(() => {
    setMenuOpen(false);
    const id = groupIdForPath(pathname);
    if (!id) return;
    setOpenGroups((current) => (current[id] ? current : { ...current, [id]: true }));
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const signOut = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate({ to: '/login' });
      },
    });
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((current) => ({ ...current, [id]: !current[id] }));
  };

  if (!isReady) {
    return (
      <AdminFrame>
        <div className="flex min-h-screen w-full items-center justify-center">
          <Spinner />
        </div>
      </AdminFrame>
    );
  }

  if (isReady && !isAuthed && !isAnon) {
    return (
      <AdminFrame>
        <div className="flex min-h-screen w-full flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-red-600">
            Could not verify session: {meError?.detail ?? meError?.message ?? 'Unknown error'}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              void refetchSession();
            }}
          >
            Retry
          </Button>
        </div>
      </AdminFrame>
    );
  }

  if (!isAuthed) {
    return null;
  }

  const activeTab = allTabs.find((tab) => tab.match(pathname));

  return (
    <AdminFrame>
      <aside className="sticky top-0 hidden h-screen shrink-0 border-r border-nova-border lg:flex">
        <SidebarNav
          pathname={pathname}
          collapsed={collapsed}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          onSignOut={signOut}
          signingOut={logout.isPending}
        />
      </aside>

      {menuOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" aria-hidden onClick={() => setMenuOpen(false)} />
          <div
            id="admin-mobile-nav"
            className="absolute inset-y-0 left-0 z-10 border-r border-nova-border bg-nova-sidebar shadow-lg"
          >
            <div className="flex h-12 items-center justify-end px-2">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-nova-foreground hover:bg-nova-accent"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              >
                <IconX size={18} />
              </button>
            </div>
            <div className="h-[calc(100%-3rem)]">
              <SidebarNav
                pathname={pathname}
                collapsed={false}
                openGroups={openGroups}
                onToggleGroup={toggleGroup}
                onNavigate={() => setMenuOpen(false)}
                onSignOut={signOut}
                signingOut={logout.isPending}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-nova-border px-3">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-nova-foreground hover:bg-nova-accent lg:hidden"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="admin-mobile-nav"
            onClick={() => setMenuOpen(true)}
          >
            <IconMenu size={18} />
          </button>
          <button
            type="button"
            className="hidden h-8 items-center rounded-lg px-2 text-sm text-nova-muted-foreground hover:bg-nova-accent hover:text-nova-foreground lg:inline-flex"
            aria-pressed={collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
          {activeTab ? (
            <span className="truncate text-sm font-medium text-nova-foreground">{activeTab.label}</span>
          ) : null}
        </header>
        <main className="min-w-0 flex-1 overflow-x-auto p-4">
          <div
            className={clsx(
              'mx-auto w-full min-w-0',
              pathname.startsWith('/admin/village-maps') ? 'max-w-[1800px]' : 'max-w-7xl'
            )}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </AdminFrame>
  );
}

export const Route = createFileRoute('/admin')({ component: AdminLayout });
